import React from 'react'
import { motion } from 'framer-motion'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { 
  FiMenu,
  FiStar,
  FiEye,
  FiEyeOff,
  FiEdit3,
  FiTrash2
} from 'react-icons/fi'
import { Artist } from '../../types/artist'

interface DraggableArtistItemProps {
  artist: Artist
  onToggleFeatured: (artistId: string, featured: boolean) => void
  onToggleActive: (artistId: string, active: boolean) => void
  onEdit: (artistId: string) => void
  onDelete: (artistId: string, artistName: string) => void
}

const DraggableArtistItem: React.FC<DraggableArtistItemProps> = ({
  artist,
  onToggleFeatured,
  onToggleActive,
  onEdit,
  onDelete
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: artist.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 bg-white border border-neutral-200 rounded-xl hover:shadow-md transition-all duration-200 ${
        isDragging ? 'shadow-lg z-50 opacity-90' : ''
      }`}
    >
      <div className="flex items-center space-x-4">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="p-2 cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <FiMenu size={16} />
        </div>

        {/* Artist Image */}
        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={artist.image}
            alt={artist.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Artist Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-semibold text-neutral-800 truncate">
              {artist.name}
            </h3>
            {artist.featured && (
              <FiStar className="text-yellow-500 fill-current flex-shrink-0" size={14} />
            )}
            {!artist.active && (
              <span className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-full flex-shrink-0">
                Inactive
              </span>
            )}
          </div>
          <p className="text-sm text-primary">{artist.genre}</p>
        </div>

        {/* Order Number */}
        <div className="text-sm text-neutral-500 font-mono">
          #{artist.order + 1}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onToggleFeatured(artist.id, artist.featured)}
            className={`p-2 rounded-lg transition-colors ${
              artist.featured
                ? 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200'
                : 'text-neutral-400 hover:text-yellow-600 hover:bg-yellow-50'
            }`}
            title={artist.featured ? 'Remove from featured' : 'Mark as featured'}
          >
            <FiStar size={14} className={artist.featured ? 'fill-current' : ''} />
          </button>

          <button
            onClick={() => onToggleActive(artist.id, artist.active)}
            className={`p-2 rounded-lg transition-colors ${
              artist.active
                ? 'text-green-600 bg-green-100 hover:bg-green-200'
                : 'text-red-600 bg-red-100 hover:bg-red-200'
            }`}
            title={artist.active ? 'Deactivate artist' : 'Activate artist'}
          >
            {artist.active ? <FiEye size={14} /> : <FiEyeOff size={14} />}
          </button>

          <button
            onClick={() => onEdit(artist.id)}
            className="p-2 rounded-lg text-neutral-600 hover:text-primary hover:bg-neutral-100 transition-colors"
            title="Edit artist"
          >
            <FiEdit3 size={14} />
          </button>

          <button
            onClick={() => onDelete(artist.id, artist.name)}
            className="p-2 rounded-lg text-neutral-600 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete artist"
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

interface DraggableArtistListProps {
  artists: Artist[]
  onReorder: (reorderedArtists: Artist[]) => void
  onToggleFeatured: (artistId: string, featured: boolean) => void
  onToggleActive: (artistId: string, active: boolean) => void
  onEdit: (artistId: string) => void
  onDelete: (artistId: string, artistName: string) => void
}

const DraggableArtistList: React.FC<DraggableArtistListProps> = ({
  artists,
  onReorder,
  onToggleFeatured,
  onToggleActive,
  onEdit,
  onDelete
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = artists.findIndex((item) => item.id === active.id)
      const newIndex = artists.findIndex((item) => item.id === over?.id)

      const reorderedArtists = arrayMove(artists, oldIndex, newIndex).map((artist, index) => ({
        ...artist,
        order: index
      }))

      onReorder(reorderedArtists)
    }
  }

  if (artists.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500">
        <p>No artists to display</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-neutral-600">
          Drag and drop to reorder artists. Changes are saved automatically.
        </p>
        <span className="text-xs text-neutral-500">
          {artists.length} artist{artists.length !== 1 ? 's' : ''}
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={artists.map(a => a.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {artists.map((artist) => (
              <DraggableArtistItem
                key={artist.id}
                artist={artist}
                onToggleFeatured={onToggleFeatured}
                onToggleActive={onToggleActive}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

export default DraggableArtistList
